/**
 * useSku — resolves a single SKU by code, piggybacking on the ['skus'] cache
 * populated by useSkus. Uses TanStack Query's `select` option to find the
 * matching entry without a separate network request when the cache is warm.
 *
 * If the cache is empty (e.g. deep-link to /sku/:code without visiting /catalog
 * first), the same queryFn from useSkus runs automatically because the key is
 * identical — TanStack Query will fire the fetch and the select will run on the
 * resolved data.
 *
 * Returns:
 *   sku         — Sku if found, null if code not in list, undefined while loading
 *   isLoading   — true until the first data or error arrives
 *   isError     — true if the fetch failed
 *   refetch     — manually trigger a fresh fetch
 */

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/api-client';
import { useAuth } from '../store/auth';
import type { Sku } from '../types/sku';

interface SkusEnvelope {
  skus: Sku[];
}

export function useSku(code: string): {
  sku: Sku | null | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const jwt = useAuth((s) => s.jwt);

  const query = useQuery<Sku[], Error, Sku | null>({
    queryKey: ['skus'],
    queryFn: async () => {
      if (!jwt) throw new Error('not_authenticated');
      const res = await apiFetch('/api/v1/skus', jwt);
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`skus_fetch_failed: ${res.status} ${body}`);
      }
      const envelope = (await res.json()) as SkusEnvelope;
      return envelope.skus;
    },
    select: (list) => list.find((s) => s.code === code) ?? null,
    enabled: !!jwt,
    staleTime: 60_000,
  });

  return {
    sku: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
